import React, { useMemo } from "react";

export interface ReceiptViewer {
  username: string;
  userEmoji: string;
}

interface ReadReceiptsProps {
  /** viewers whose last-read message is this bubble (Messenger "last-seen" style) */
  viewers: ReceiptViewer[];
  /** how many emojis to show before collapsing the rest into "+N" */
  max?: number;
}

// shown when a viewer has no profile emoji set
const FALLBACK_EMOJI = "👤";

/**
 * compact, right-aligned row of read-receipt emojis rendered under a message bubble.
 * One emoji per viewer (deduped by username); overflow past `max` collapses to "+N".
 */
export function ReadReceipts({ viewers, max = 5 }: ReadReceiptsProps) {
  const unique = useMemo(() => {
    const seen = new Set<string>();
    const out: ReceiptViewer[] = [];
    for (const v of viewers) {
      if (seen.has(v.username)) continue;
      seen.add(v.username);
      out.push(v);
    }
    return out;
  }, [viewers]);

  if (unique.length === 0) return null;

  const shown = unique.slice(0, max);
  const overflow = unique.length - shown.length;

  return (
    <div className="flex justify-end items-center gap-0.5 pr-1 mt-0.5 leading-none">
      {shown.map((v) => (
        <span
          key={v.username}
          title={`seen by ${v.username}`}
          className="text-[11px] md:text-xs"
        >
          {v.userEmoji || FALLBACK_EMOJI}
        </span>
      ))}
      {overflow > 0 && (
        <span className="text-[9px] md:text-[10px] text-gray-400 font-bold">
          +{overflow}
        </span>
      )}
    </div>
  );
}
